import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export interface EmailFolder {
  id: string;
  name: string;
  color: string;
  icon: string;
  display_order: number;
  is_default: boolean;
}

export interface Email {
  id: string;
  folder_id: string | null;
  sender_name: string;
  sender_email: string;
  subject: string;
  preview: string;
  body: string;
  is_read: boolean;
  is_starred: boolean;
  category: string;
  received_at: string;
}

export interface EmailDraft {
  id: string;
  email_id: string | null;
  draft_body: string;
  status: string;
  created_at: string;
}

// Map Gmail folder names to nice French labels + icons
const gmailFolderMap: Record<string, { name: string; icon: string; color: string }> = {
  "INBOX": { name: "Boite de réception", icon: "Inbox", color: "hsl(220 70% 55%)" },
  "[Gmail]/Messages envoyés": { name: "Envoyés", icon: "Send", color: "hsl(160 84% 39%)" },
  "[Gmail]/Sent Mail": { name: "Envoyés", icon: "Send", color: "hsl(160 84% 39%)" },
  "[Gmail]/Tous les messages": { name: "Tous les messages", icon: "Mail", color: "hsl(270 76% 55%)" },
  "[Gmail]/All Mail": { name: "Tous les messages", icon: "Mail", color: "hsl(270 76% 55%)" },
  "[Gmail]/Brouillons": { name: "Brouillons", icon: "Pencil", color: "hsl(45 93% 47%)" },
  "[Gmail]/Drafts": { name: "Brouillons", icon: "Pencil", color: "hsl(45 93% 47%)" },
  "[Gmail]/Spam": { name: "Spam", icon: "Trash2", color: "hsl(0 84% 60%)" },
  "[Gmail]/Corbeille": { name: "Corbeille", icon: "Trash2", color: "hsl(0 60% 50%)" },
  "[Gmail]/Trash": { name: "Corbeille", icon: "Trash2", color: "hsl(0 60% 50%)" },
  "[Gmail]/Suivis": { name: "Suivis", icon: "Star", color: "hsl(45 93% 47%)" },
  "[Gmail]/Starred": { name: "Suivis", icon: "Star", color: "hsl(45 93% 47%)" },
  "[Gmail]/Important": { name: "Important", icon: "Bell", color: "hsl(25 95% 53%)" },
};

async function apiFetch(path: string, options?: RequestInit) {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: "include",
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Erreur réseau" }));
    throw new Error(err.detail || `Erreur ${res.status}`);
  }
  return res.json();
}

export function useEmailData(userId: string | undefined) {
  const [folders, setFolders] = useState<EmailFolder[]>([]);
  const [emails, setEmails] = useState<Email[]>([]);
  const [drafts] = useState<EmailDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [analysisActive, setAnalysisActive] = useState(false);
  const [gmailConnected, setGmailConnected] = useState(true);
  const [totalEmails, setTotalEmails] = useState(0);

  const loadFolders = useCallback(async () => {
    try {
      const data = await apiFetch("/api/gmail/folders");
      const allFolders = [...(data.folders || []), ...(data.gmail_folders || [])];

      // Build folder list with nice names
      const builtFolders: EmailFolder[] = [];
      // Always add INBOX first
      builtFolders.push({
        id: "INBOX",
        name: "Boite de réception",
        icon: "Inbox",
        color: "hsl(220 70% 55%)",
        display_order: 0,
        is_default: true,
      });

      let order = 1;
      const seen = new Set(["INBOX"]);
      for (const fname of allFolders) {
        if (seen.has(fname)) continue;
        seen.add(fname);
        const mapped = gmailFolderMap[fname];
        if (mapped) {
          builtFolders.push({
            id: fname,
            name: mapped.name,
            icon: mapped.icon,
            color: mapped.color,
            display_order: order++,
            is_default: true,
          });
        }
      }

      setFolders(builtFolders);
      setGmailConnected(true);
    } catch (err: any) {
      console.error("Gmail folders error:", err);
      if (err.message?.includes("non connecté")) {
        setGmailConnected(false);
      }
      // Provide default INBOX folder even on error
      setFolders([{
        id: "INBOX",
        name: "Boite de réception",
        icon: "Inbox",
        color: "hsl(220 70% 55%)",
        display_order: 0,
        is_default: true,
      }]);
    }
  }, []);

  const loadEmails = useCallback(async (folder: string = "INBOX", limit: number = 50) => {
    setLoading(true);
    try {
      const data = await apiFetch(`/api/gmail/emails?folder=${encodeURIComponent(folder)}&limit=${limit}`);
      setEmails(data.emails || []);
      setTotalEmails(data.total || 0);
      setGmailConnected(true);
    } catch (err: any) {
      console.error("Gmail emails error:", err);
      if (err.message?.includes("non connecté")) {
        setGmailConnected(false);
      }
      setEmails([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!userId) return;
    loadFolders();
    loadEmails("INBOX");
  }, [userId, loadFolders, loadEmails]);

  const loadEmailsByFolder = useCallback(async (folderId: string) => {
    await loadEmails(folderId);
  }, [loadEmails]);

  // Get full email detail
  const getEmailDetail = useCallback(async (emailId: string, folder: string = "INBOX"): Promise<Email | null> => {
    try {
      const data = await apiFetch(`/api/gmail/email/${emailId}?folder=${encodeURIComponent(folder)}`);
      return {
        id: data.id,
        folder_id: folder,
        sender_name: data.sender_name,
        sender_email: data.sender_email,
        subject: data.subject,
        preview: "",
        body: data.body,
        is_read: data.is_read,
        is_starred: false,
        category: "general",
        received_at: data.received_at,
      };
    } catch {
      return null;
    }
  }, []);

  const markAsRead = useCallback((id: string) => {
    setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, is_read: true } : e)));
  }, []);

  const toggleStar = useCallback((id: string) => {
    setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, is_starred: !e.is_starred } : e)));
  }, []);

  const getEmailsByFolder = useCallback((folderId: string) => {
    return emails.filter((e) => e.folder_id === folderId);
  }, [emails]);

  const getCategoryStats = useCallback(() => {
    const cats: Record<string, number> = {};
    emails.forEach((e) => { cats[e.category] = (cats[e.category] || 0) + 1; });
    return cats;
  }, [emails]);

  // Folder management (local only - not persisted to Gmail)
  const addFolder = useCallback((name: string, color: string) => {
    const id = `custom-${Date.now()}`;
    setFolders((prev) => [...prev, {
      id, name, color, icon: "Folder", display_order: prev.length, is_default: false,
    }]);
    toast.success(`Dossier "${name}" créé`);
  }, []);

  const updateFolder = useCallback((id: string, updates: Partial<EmailFolder>) => {
    setFolders((prev) => prev.map((f) => (f.id === id ? { ...f, ...updates } : f)));
  }, []);

  const deleteFolder = useCallback((id: string) => {
    setFolders((prev) => prev.filter((f) => f.id !== id));
    toast.success("Dossier supprimé");
  }, []);

  const approveDraft = useCallback((draftId: string) => {
    toast.success("Réponse validée et envoyée !");
  }, []);

  const rejectDraft = useCallback((draftId: string) => {
    toast.info("Brouillon rejeté");
  }, []);

  return {
    folders, emails, drafts, loading, analysisActive, setAnalysisActive,
    gmailConnected, totalEmails,
    addFolder, updateFolder, deleteFolder,
    markAsRead, toggleStar, approveDraft, rejectDraft,
    getEmailsByFolder, getCategoryStats,
    loadEmailsByFolder, getEmailDetail,
    reload: () => { loadFolders(); loadEmails("INBOX"); },
  };
}
